/**
 * Unified status handling for crawlers, jobs, and requests
 * Handles both new object format { label, color, state } and legacy string format
 */

export interface StatusDisplay {
  label: string;
  color: string;
  state: string;
}

export type StatusLevel = 'crawler' | 'job' | 'request';

/**
 * Normalize any status format to the standard StatusDisplay object
 */
export const formatStatusDisplay = (status: any): StatusDisplay => {
  // Handle new object format from backend
  if (status && typeof status === 'object' && status.label) {
    return {
      label: status.label,
      color: status.color || 'gray',
      state: status.state || getStateFromLabel(status.label)
    };
  }

  // Handle legacy string format
  const statusStr = typeof status === 'string' ? status.toLowerCase() : '';

  // Map legacy statuses to new format
  return mapLegacyStatus(statusStr);
};

/**
 * Get the state from a label (running, scheduled, done, failed, etc.)
 */
const getStateFromLabel = (label: string): string => {
  const lowerLabel = label.toLowerCase();

  if (lowerLabel.includes('running') || lowerLabel.includes('processing') || lowerLabel.includes('progress')) {
    return 'running';
  }
  if (lowerLabel.includes('scheduled') || lowerLabel.includes('queued')) {
    return 'scheduled';
  }
  if (lowerLabel.includes('done') || lowerLabel.includes('completed') || lowerLabel.includes('delivered') || lowerLabel.includes('success')) {
    return 'done';
  }
  if (lowerLabel.includes('failed') || lowerLabel.includes('error')) {
    return 'failed';
  }
  if (lowerLabel.includes('cancelled') || lowerLabel.includes('stopped') || lowerLabel.includes('not running')) {
    return 'cancelled';
  }

  return 'unknown';
};

/**
 * Map status strings to StatusDisplay objects
 * Backend returns normalized states: running, completed, failed, cancelled
 */
const mapLegacyStatus = (status: string): StatusDisplay => {
  const normalizedStatus = status.toLowerCase().trim();

  // Map canonical states from backend to UI display
  switch (normalizedStatus) {
    case 'running':
      return { label: 'Running', color: 'blue', state: 'running' };

    case 'completed':
      return { label: 'Done', color: 'green', state: 'done' };

    case 'failed':
      return { label: 'Failed', color: 'red', state: 'failed' };

    case 'cancelled':
      return { label: 'Cancelled', color: 'gray', state: 'cancelled' };

    case 'scheduled':
      return { label: 'Scheduled', color: 'purple', state: 'scheduled' };

    default:
      return { label: status || 'Unknown', color: 'gray', state: 'unknown' };
  }
};

/**
 * Check if a status represents a running state
 */
export const isStatusRunning = (status: any): boolean => {
  const normalized = formatStatusDisplay(status);
  return normalized.state === 'running';
};

/**
 * Check if a status represents a completed state
 */
export const isStatusCompleted = (status: any): boolean => {
  const normalized = formatStatusDisplay(status);
  return normalized.state === 'done';
};

/**
 * Check if a status represents a failed state
 */
export const isStatusFailed = (status: any): boolean => {
  const normalized = formatStatusDisplay(status);
  return normalized.state === 'failed';
};

/**
 * Check if a status represents a scheduled state
 */
export const isStatusScheduled = (status: any): boolean => {
  const normalized = formatStatusDisplay(status);
  return normalized.state === 'scheduled';
};

/**
 * Check if a status represents a cancelled state
 */
export const isStatusCancelled = (status: any): boolean => {
  const normalized = formatStatusDisplay(status);
  return normalized.state === 'cancelled';
};

/**
 * Get a human-readable status label
 */
export const getStatusLabel = (status: any): string => {
  return formatStatusDisplay(status).label;
};

/**
 * Get the color for a status
 */
export const getStatusColor = (status: any): string => {
  return formatStatusDisplay(status).color;
};

/**
 * Get the state for a status
 */
export const getStatusState = (status: any): string => {
  return formatStatusDisplay(status).state;
};

/**
 * Order statuses by priority for sorting
 */
export const STATUS_PRIORITY: Record<string, number> = {
  running: 1,
  scheduled: 2,
  done: 3,
  failed: 4,
  cancelled: 5,
  unknown: 99
};

/**
 * Compare two statuses for sorting
 */
export const compareStatuses = (statusA: any, statusB: any): number => {
  const stateA = formatStatusDisplay(statusA).state;
  const stateB = formatStatusDisplay(statusB).state;

  const priorityA = STATUS_PRIORITY[stateA] ?? STATUS_PRIORITY.unknown;
  const priorityB = STATUS_PRIORITY[stateB] ?? STATUS_PRIORITY.unknown;

  return priorityA - priorityB;
};
